import React from 'react';
import { Box, Card, Text, Title } from '@mantine/core';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';

import { Shield, AlertTriangle } from 'lucide-react';

interface StaffGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Gate for STAFF-only surfaces (`is_staff` OR `is_superuser`) — the frontend
 * mirror of the backend's ``require_access(staff=True)``. Mirrors
 * `SuperAdminGuard`, which stays the gate for superuser-only surfaces.
 *
 * ``/crm/users/me/`` does expose ``is_staff`` (``UserMeSchema``), so this
 * admits real staff and not only superusers. The field stays optional on the
 * `User` type for older cached payloads; a missing value reads as false, which
 * is the correct failure direction (the backend 403s non-staff regardless).
 *
 * The nav counterpart is the ``staffOnly`` flag in ``navConfig.ts``. A route
 * wrapped here must carry that flag on its leaf and vice versa —
 * ``navConfig.test.ts`` enforces the pairing in both directions.
 */
export const StaffGuard: React.FC<StaffGuardProps> = ({ children, fallback }) => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!(user?.is_staff || user?.is_superuser)) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <Box mih="100vh" display="flex" style={{ alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--mantine-color-body)' }}>
        <Card withBorder padding="lg" w="100%" maw={448}>
          <Box ta="center" p="1rem">
            <Box ml="auto" mr="auto" mb={16} h={48} w={48} display="flex" style={{ alignItems: 'center', justifyContent: 'center', borderRadius: 9999, backgroundColor: 'var(--mantine-color-red-1)' }}>
              <Shield style={{ height: 24, width: 24, color: 'var(--mantine-color-red-6)' }} />
            </Box>
            <Title order={4} fz="xl">Access Denied</Title>
            <Text size="sm" c="dimmed">
              This page requires staff privileges.
            </Text>
          </Box>
          <Box ta="center" p="0 1rem 1rem">
            <Box fz="sm" c="dimmed" mb={16} display="flex" style={{ alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <AlertTriangle style={{ height: 16, width: 16 }} />
              <span>Staff access required</span>
            </Box>
            <Text component="p" fz="sm" c="dimmed">
              Please contact your administrator if you believe you should have access to this page.
            </Text>
          </Box>
        </Card>
      </Box>
    );
  }

  return <>{children}</>;
};
